import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { TrustBannerEditor } from '@/components/TrustBanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Save, Loader2, Plus, Trash2, GripVertical, Eye,
  Sparkles, BarChart3, Star, CreditCard, MessageSquareQuote,
  ArrowRight, Layout,
} from 'lucide-react';

const HomepageEditor = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await api.get('/cms/homepage');
      setData(res.data);
    } catch {
      toast.error('Failed to load homepage content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/cms/homepage', data);
      setData(res.data);
      toast.success('Homepage updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (keys[i].match(/^\d+$/)) obj = obj[parseInt(keys[i])];
        else obj = obj[keys[i]];
      }
      const lastKey = keys[keys.length - 1];
      obj[lastKey.match(/^\d+$/) ? parseInt(lastKey) : lastKey] = value;
      return next;
    });
  };

  if (loading || !data) {
    return (
      <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
        <TrustBannerEditor />
    </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="homepage-editor">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Edit Homepage</h1>
            <p className="text-slate-500 mt-1">Customise your homepage sections</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild data-testid="preview-homepage-btn">
              <a href="/" target="_blank"><Eye className="h-4 w-4 mr-2" /> Preview</a>
            </Button>
            <Button
              className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-homepage-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex flex-wrap gap-1">
            <TabsTrigger value="hero" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5 text-xs sm:text-sm">
              <Sparkles className="h-3.5 w-3.5" /> Hero
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5 text-xs sm:text-sm">
              <BarChart3 className="h-3.5 w-3.5" /> Stats
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5 text-xs sm:text-sm">
              <Layout className="h-3.5 w-3.5" /> Features
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5 text-xs sm:text-sm">
              <MessageSquareQuote className="h-3.5 w-3.5" /> Testimonials
            </TabsTrigger>
            <TabsTrigger value="cta" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5 text-xs sm:text-sm">
              <ArrowRight className="h-3.5 w-3.5" /> CTA
            </TabsTrigger>
            <TabsTrigger value="footer" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5 text-xs sm:text-sm">
              <CreditCard className="h-3.5 w-3.5" /> Footer
            </TabsTrigger>
          </TabsList>

          {/* ---- HERO ---- */}
          <TabsContent value="hero">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg text-slate-900">Hero Section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Badge Text" value={data.hero?.badge_text} onChange={v => updateField('hero.badge_text', v)} testId="hero-badge" />
                <Field label="Headline" value={data.hero?.headline} onChange={v => updateField('hero.headline', v)} testId="hero-headline" />
                <Field label="Headline Accent (coloured word)" value={data.hero?.headline_accent} onChange={v => updateField('hero.headline_accent', v)} testId="hero-accent" />
                <FieldArea label="Subheadline" value={data.hero?.subheadline} onChange={v => updateField('hero.subheadline', v)} testId="hero-sub" rows={3} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Primary CTA Text" value={data.hero?.cta_text} onChange={v => updateField('hero.cta_text', v)} testId="hero-cta-text" />
                  <Field label="Primary CTA Link" value={data.hero?.cta_link} onChange={v => updateField('hero.cta_link', v)} testId="hero-cta-link" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Secondary CTA Text" value={data.hero?.secondary_cta_text} onChange={v => updateField('hero.secondary_cta_text', v)} testId="hero-cta2-text" />
                  <Field label="Secondary CTA Link" value={data.hero?.secondary_cta_link} onChange={v => updateField('hero.secondary_cta_link', v)} testId="hero-cta2-link" />
                </div>
                <Field label="Background Image URL (optional)" value={data.hero?.background_image_url || ''} onChange={v => updateField('hero.background_image_url', v || null)} testId="hero-bg-img" placeholder="https://images.unsplash.com/..." />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- STATS ---- */}
          <TabsContent value="stats">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-slate-900">Stats Section</CardTitle>
                <Button size="sm" variant="outline" onClick={() => {
                  const stats = [...(data.stats || []), { value: '', label: '' }];
                  updateField('stats', stats);
                }} data-testid="add-stat-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Stat</Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.stats || []).map((stat, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3" data-testid={`stat-row-${i}`}>
                    <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                    <Input value={stat.value} onChange={e => updateField(`stats.${i}.value`, e.target.value)} placeholder="500+" className="w-32" data-testid={`stat-value-${i}`} />
                    <Input value={stat.label} onChange={e => updateField(`stats.${i}.label`, e.target.value)} placeholder="Label" className="flex-1" data-testid={`stat-label-${i}`} />
                    <Button size="icon" variant="ghost" onClick={() => {
                      const stats = data.stats.filter((_, idx) => idx !== i);
                      updateField('stats', stats);
                    }} data-testid={`del-stat-${i}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- FEATURES ---- */}
          <TabsContent value="features">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900">Features Section</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Section Heading" value={data.features_heading} onChange={v => updateField('features_heading', v)} testId="features-heading" />
                <FieldArea label="Section Subheading" value={data.features_subheading} onChange={v => updateField('features_subheading', v)} testId="features-sub" rows={2} />
                <div className="flex items-center justify-between pt-2">
                  <Label className="text-sm font-medium text-slate-700">Feature Cards</Label>
                  <Button size="sm" variant="outline" onClick={() => {
                    const features = [...(data.features || []), { title: '', description: '', icon: 'BookOpen' }];
                    updateField('features', features);
                  }} data-testid="add-feature-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                </div>
                {(data.features || []).map((f, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid={`feature-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-[#20acf6]/10 text-[#20acf6] text-xs">Feature {i + 1}</Badge>
                      <Button size="icon" variant="ghost" onClick={() => {
                        updateField('features', data.features.filter((_, idx) => idx !== i));
                      }} data-testid={`del-feature-${i}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input value={f.title} onChange={e => updateField(`features.${i}.title`, e.target.value)} placeholder="Feature title" data-testid={`feature-title-${i}`} />
                      <Input value={f.icon} onChange={e => updateField(`features.${i}.icon`, e.target.value)} placeholder="Icon name (e.g. BookOpen)" data-testid={`feature-icon-${i}`} />
                    </div>
                    <Textarea value={f.description} onChange={e => updateField(`features.${i}.description`, e.target.value)} placeholder="Description" rows={2} data-testid={`feature-desc-${i}`} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- TESTIMONIALS ---- */}
          <TabsContent value="testimonials">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-slate-900">Testimonials</CardTitle>
                <Button size="sm" variant="outline" onClick={() => {
                  const testimonials = [...(data.testimonials || []), {
                    id: crypto.randomUUID ? crypto.randomUUID() : `t-${Date.now()}`,
                    quote: '', author_name: '', author_role: '', organisation: '', rating: 5
                  }];
                  updateField('testimonials', testimonials);
                }} data-testid="add-testimonial-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Testimonial</Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {(!data.testimonials || data.testimonials.length === 0) && (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquareQuote className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No testimonials yet. Add one to showcase on the homepage.</p>
                  </div>
                )}
                {(data.testimonials || []).map((t, i) => (
                  <div key={t.id || i} className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid={`testimonial-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-[#02d1d4]/10 text-[#02d1d4] text-xs">Testimonial {i + 1}</Badge>
                      <Button size="icon" variant="ghost" onClick={() => {
                        updateField('testimonials', data.testimonials.filter((_, idx) => idx !== i));
                      }} data-testid={`del-testimonial-${i}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </div>
                    <FieldArea value={t.quote} onChange={v => updateField(`testimonials.${i}.quote`, v)} placeholder="What they said..." rows={3} testId={`testimonial-quote-${i}`} />
                    <div className="grid sm:grid-cols-3 gap-3">
                      <Input value={t.author_name} onChange={e => updateField(`testimonials.${i}.author_name`, e.target.value)} placeholder="Name" data-testid={`testimonial-name-${i}`} />
                      <Input value={t.author_role} onChange={e => updateField(`testimonials.${i}.author_role`, e.target.value)} placeholder="Role" data-testid={`testimonial-role-${i}`} />
                      <Input value={t.organisation} onChange={e => updateField(`testimonials.${i}.organisation`, e.target.value)} placeholder="Organisation" data-testid={`testimonial-org-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Rating</Label>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(s => (
                          <button key={s} type="button" onClick={() => updateField(`testimonials.${i}.rating`, s)} data-testid={`testimonial-star-${i}-${s}`}>
                            <Star className={`h-5 w-5 ${s <= (t.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- CTA ---- */}
          <TabsContent value="cta">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg text-slate-900">Call-to-Action Section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="CTA Heading" value={data.cta_heading} onChange={v => updateField('cta_heading', v)} testId="cta-heading" />
                <FieldArea label="CTA Subheading" value={data.cta_subheading} onChange={v => updateField('cta_subheading', v)} testId="cta-sub" rows={2} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- FOOTER ---- */}
          <TabsContent value="footer">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg text-slate-900">Footer Content</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <FieldArea label="Company Description" value={data.footer?.company_description} onChange={v => updateField('footer.company_description', v)} testId="footer-desc" rows={2} />
                <Field label="Copyright Text" value={data.footer?.copyright_text} onChange={v => updateField('footer.copyright_text', v)} testId="footer-copyright" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Contact Email" value={data.footer?.contact_email} onChange={v => updateField('footer.contact_email', v)} testId="footer-email" placeholder="info@commonimpact.co.uk" />
                  <Field label="Contact Phone" value={data.footer?.contact_phone} onChange={v => updateField('footer.contact_phone', v)} testId="footer-phone" placeholder="+44 ..." />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

// Reusable form field components
const Field = ({ label, value, onChange, testId, placeholder }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-sm text-slate-700">{label}</Label>}
    <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </div>
);

const FieldArea = ({ label, value, onChange, testId, rows = 3, placeholder }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-sm text-slate-700">{label}</Label>}
    <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testId} />
  </div>
);

export default HomepageEditor;
